'use strict';

import { action, configure, observable, observe, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {States} from "@uw-it-sis/lib-react/lib/AppConstants";
import {getAggregateState} from "@uw-it-sis/lib-react/lib/StateUtils";
import registrationDateStore from '@uw-it-sis/lib-react-myplan/lib/RegistrationDateStore';
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import events from "@uw-it-sis/lib-react/lib/Events";
import PlanEvents from "@uw-it-sis/lib-react-myplan/lib/PlanEvents";
import AnnouncerStore from "@uw-it-sis/lib-react/lib/AnnouncerStore";

import scheduleBuilderOptionsStore from './ScheduleBuilderOptionsStore';
import scheduleBuilderPlanStore from './ScheduleBuilderPlanStore';
import reservedTimeFilterStore from './ReservedTimeFilterStore';
import pinnedSchedulesStore from './PinnedSchedulesStore';
import registrationDetailsStore from '../registration/RegistrationDetailsStore';
import appStore from "@uw-it-sis/lib-react/lib/AppStore";

class ScheduleBuilderDetailsStore {
    state = States.initial;
    formData = this.defaultFormData();

    constructor() {
        makeObservable(this, {
            state: observable,
            formData: observable,
            mount: action,
            resetCourseInclusionSelections: action,
            updateAppliedFilters: action,
            updateCourseInclusionSelection: action,
            checkDataLoadStates: action
        });

        this.componentKey = "ScheduleBuilderDetails";

        this.termId = '';
        this.dataLoaded = false;

        this.dataLoadStates = {
            planData: States.initial,
            reservedTimeFilters: States.initial,
            pinnedSchedules: States.initial,
            registrationDate: States.initial,
            registrationDetails: States.initial
        };
        this.urlPersistenceStore = appStore.getUrlPersistenceStore();
        // Register the store for URL persistence.
        this.urlPersistenceStore.register(this.componentKey, this.defaultFormData);
    }

    mount(termId, planDataOnly) {
        const formData = this.urlPersistenceStore.getPersistedData(this.componentKey);
        if (formData) {
            this.formData = formData;
        }

        this.termId = termId;
        this.dataLoaded = false;

        if (planDataOnly) {
            this.dataLoadStates.planData = States.pending;

            this.planDataDisposer = observe(scheduleBuilderPlanStore, "state", function (change) {
                this.dataLoadStates.planData = change.newValue;
                this.checkDataLoadStates();
            }.bind(this));

            scheduleBuilderPlanStore.loadData(termId);
        } else {
            this.dataLoadStates = {
                planData: States.pending,
                reservedTimeFilters: States.pending,
                pinnedSchedules: States.pending,
                registrationDate: States.pending,
                registrationDetails: States.pending
            };

            this.planDataDisposer = observe(scheduleBuilderPlanStore, "state", function (change) {
                this.dataLoadStates.planData = change.newValue;
                this.checkDataLoadStates();
            }.bind(this));

            scheduleBuilderPlanStore.loadData(termId);

            this.reservedTimeFiltersDisposer = observe(reservedTimeFilterStore, "state", function (change) {
                this.dataLoadStates.reservedTimeFilters = change.newValue;
                this.checkDataLoadStates();
            }.bind(this));

            reservedTimeFilterStore.loadReservedTimeFilters(termId);

            this.pinnedSchedulesDisposer = observe(pinnedSchedulesStore, "state", function (change) {
                this.dataLoadStates.pinnedSchedules = change.newValue;
                this.checkDataLoadStates();
            }.bind(this));

            pinnedSchedulesStore.loadPinnedSchedules(termId, true, "SB-load-pinned");

            this.registrationDateDisposer = observe(registrationDateStore, "state", function (change) {
                this.dataLoadStates.registrationDate = change.newValue;
                this.checkDataLoadStates();
            }.bind(this));

            registrationDateStore.mount();

            this.registrationDetailsDisposer = observe(registrationDetailsStore, "state", function (change) {
                this.dataLoadStates.registrationDetails = change.newValue;
                this.checkDataLoadStates();
            }.bind(this));

            registrationDetailsStore.mount(termId);
        }

        // Push the current state up to browser history, stripping out the 'src' param.
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.formData,
            ["src"]);

        //this is to register the listener only once
        if (!planDataOnly) {
            events.listen(PlanEvents.planItemAdded, (eo) => {
                if (eo.code === "SB_ADD_REMOVE") {
                    AnnouncerStore.announce(`${eo.description} has been added in ${TermUtils.makeTermDescriptionFromTermId(eo.atpId)}`);
                    if (eo.data) {
                        eo.data.map(code => {
                            this.updateCourseInclusionSelection(code, true);
                        });
                    }
                    // Clear out the saved user selections
                    this.resetCourseInclusionSelections();
                    this.mount(termId, true);
                }
            });
        }
        this.checkDataLoadStates();
    }

    refreshData() {
        this.mount(this.termId);
    }

    unmount() {
        this.planDataDisposer();
        this.reservedTimeFiltersDisposer();
        this.pinnedSchedulesDisposer();
        this.registrationDateDisposer();
        this.registrationDetailsDisposer();
        this.unregisterEvent();
    }

    unregisterEvent() {
        events.remove(PlanEvents.planItemAdded, (eo) => {
            if (eo.code === "SB_ADD_REMOVE") {
                AnnouncerStore.announce(`${eo.description} has been added in ${TermUtils.makeTermDescriptionFromTermId(eo.atpId)}`);
                if(eo.data) {
                    eo.data.map(code => {
                        this.updateCourseInclusionSelection(code, true);
                    });
                }
                // Clear out the saved user selections
                this.resetCourseInclusionSelections();
                this.mount(termId, true);
            }
        });
    }

    defaultFormData() {
        return ({
            courseInclusionSelections: {},
            appliedFilters: {
                "showClosed": false,
                "showRestricted": true,
                "showOverlapped": false,
                "showOtherInstitutes": false
            }
        });
    }

    // Clear out the saved user selections on plan items for a fresh state (used after add all to plan action)
    resetCourseInclusionSelections() {
        this.formData.courseInclusionSelections = {};

        // Push the current form data up to browser history.
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.formData);
    }

    // Update checkbox filters in form data
    updateAppliedFilters(property, checked) {
        this.formData.appliedFilters[property] = checked;

        // Push the current form data up to browser history.
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.formData);
    }

    // Update plan item selections for sending to SB for schedule option generation
    updateCourseInclusionSelection(key, checked) {
        this.formData.courseInclusionSelections[key] = checked;

        // Push the current form data up to browser history.
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.formData);
    }

    // Gets the value of plan item selections that have been persisted for SB
    getCourseInclusionSelection(key) {
        if (this.formData.courseInclusionSelections.hasOwnProperty(key)){
            return this.formData.courseInclusionSelections[key];
        } else {
            return null;
        }
    }

    buildScheduleOptions() {
        scheduleBuilderOptionsStore.loadData(this.getAppliedFilters(), true);
    }

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);

        // Once the plan data and reserve times have been loaded, disconnect the observing of their store states
        if (this.state === States.done) {
            this.unmount();
            this.dataLoaded = true;
        }
    }

    getAppliedFilters() {
        return this.formData.appliedFilters;
    }

    getSelectedFilters() {
        let selectedFilters = [];
        if (this.formData.appliedFilters["showClosed"]){
            selectedFilters.push("showClosed");
        }
        if (this.formData.appliedFilters["showRestricted"]){
            selectedFilters.push("showRestricted");
        }
        if (this.formData.appliedFilters["showOverlapped"]){
            selectedFilters.push("showOverlapped");
        }
        if (this.formData.appliedFilters["showOtherInstitutes"]){
            selectedFilters.push("showOtherInstitutes");
        }
        return selectedFilters;
    }

    getRegistrationDate() {
        let termName = TermUtils.makeTermName(this.termId);
        return registrationDateStore.getRegistrationDateForTerm(termName);
    }

    hasData() {
        return this.termId !== null && this.dataLoaded;
    }

    isBuildScheduleOptionsDisabled() {
        return scheduleBuilderPlanStore.getCourseInclusionFilterCount() === 0;
    }
}

export default new ScheduleBuilderDetailsStore();